import http from '@/api/http'
import { subscriptionsPageLimit } from '@/config/api'
import { cursorParams, type PageMeta } from '@/api/pagination'

export interface Subscription {
  // One purchased subscription from GET `/subscriptions/{id}`.

  id: string
  // Subscription id (UUID7).

  person_id: string
  // Owner of the subscription.

  subscription_type_id: string
  // Catalog plan that was purchased (see `SubscriptionType`).

  payment_id: string
  // Payment that created the subscription.

  valid_from: string
  // ISO datetime from which the subscription is valid.

  is_active: boolean
  // Whether the subscription is currently active.

  remaining: number | null
  // Remaining covered visits; `null` means unlimited.

  is_exhausted: boolean
  // Whether the visit quota is used up.
}

/** Fetches one purchased subscription (e.g. for its start date on the payment card). */
export async function fetchSubscription(id: string): Promise<Subscription> {
  const { data } = await http.get<Subscription>(`/subscriptions/${id}`)
  if (import.meta.env.DEV)
    console.log(`[fetchSubscription] GET /subscriptions/${id} response`, data)
  return data
}

interface SubscriptionsResponse extends PageMeta {
  // Paginated list payload from GET `/subscriptions/clients/{id}`.

  items: Subscription[]
  // Page of subscriptions.
}

/**
 * Fetches a page of the subscriptions a person has purchased.
 * @param personId - Client id.
 * @param cursor - Optional; pass `next_cursor` from a previous page for the next page.
 */
export async function fetchPersonSubscriptions(
  personId: string,
  cursor?: number,
): Promise<SubscriptionsResponse> {
  const { data } = await http.get<SubscriptionsResponse>(`/subscriptions/clients/${personId}`, {
    params: cursorParams(subscriptionsPageLimit, cursor),
  })
  if (import.meta.env.DEV) {
    console.log(`[fetchPersonSubscriptions] GET /subscriptions/clients/${personId} response`, data)
  }
  return { ...data, items: data.items ?? [] }
}
